const productSubscriptions = class extends HTMLElement {
  constructor() {
    super()

    this.form = this.closest('form')
    this.sellingPlanInput = this.querySelector('[data-selling-plan-input]')
    this.variantSellingPlanGroupings = this.querySelectorAll('[data-selling-plan-variant-grouping]')
    this.variantIdInput = this.form.querySelector('input[name="id"]')
    this.sellingPlanOptionWrappers = this.querySelectorAll('[data-selling-plan-wrapper]')
    this.sellingPlanOptions = this.querySelectorAll('[data-selling-plan]')
    this.sellingPlanTypeLabel = this.querySelector('[data-selling-plan-type]')
    this.sellingPlanOptions.length && this.init()
  }

  init () {
    this.sellingPlanOptions.forEach(sellingPlanOption => {
      sellingPlanOption.addEventListener('change', () => {
        this.sellingPlanInput.value = sellingPlanOption.value
        this.sellingPlanTypeLabel.innerHTML = sellingPlanOption.dataset.frequency
        this.sellingPlanInput.name = sellingPlanOption.value ? 'selling_plan' : ''
        this.variantIdInput.value = sellingPlanOption.dataset.variantId

        this.sellingPlanOptionWrappers.forEach(sellingPlanOptionWrapper => {
          sellingPlanOptionWrapper.classList.toggle('active', sellingPlanOptionWrapper.contains(sellingPlanOption))
        })
      })
    })

    this.variantIdInput.addEventListener('change', (event) => {
      this.variantChanged(event.target.value);
    })

    // Listen for variant:change event to update one-time purchase price
    this.form.addEventListener('variant:change', (event) => {
      this.updateOneTimePrice(event.detail.variant);
    })

    // Initialize one-time purchase price on load
    this.initializeOneTimePrice();

    // Make the entire subscription option clickable
    this.sellingPlanOptionWrappers.forEach(sellingPlanOptionWrapper => {
      const list = sellingPlanOptionWrapper.querySelector('[data-selling-plan-details]')
      if (!list) { return }
      list.style.setProperty('--list-height', `${list.scrollHeight}px`);
      
      // Add click handler to the data-selling-plan-wrapper element
      sellingPlanOptionWrapper.addEventListener('click', (event) => {
        // Don't trigger if clicking directly on the radio button (to avoid double-triggering)
        if (event.target.type === 'radio') {
          return
        }
        
        // Find the radio button within this wrapper and click it
        const radioButton = sellingPlanOptionWrapper.querySelector('input[type="radio"]')
        if (radioButton) {
          radioButton.click()
        }
      })
    })
  }

  variantChanged (variantId) {
    this.variantSellingPlanGroupings.forEach(variantSellingPlanGrouping => {
      if(variantSellingPlanGrouping.dataset.variantId == variantId) {
        variantSellingPlanGrouping.classList.add('is-active');
        // Click the first subscription option for this variant to update prices
        const firstOption = variantSellingPlanGrouping.querySelector('[data-selling-plan]');
        if (firstOption) {
          firstOption.click();
        }
      } else {
        variantSellingPlanGrouping.classList.remove('is-active');
      }
    });
  }

  initializeOneTimePrice () {
    // Wait a bit for variant picker to initialize, then get initial variant
    setTimeout(() => {
      const variantId = this.variantIdInput?.value;
      if (!variantId) return;

      // Try to get variant from variant:change event if it fires
      const checkVariant = () => {
        // Check if variant picker is available and has product data
        const variantPicker = document.querySelector('variant-picker[form="' + this.form.id + '"]');
        if (variantPicker && variantPicker.product) {
          const variant = variantPicker.product.variants.find(v => v.id === parseInt(variantId));
          if (variant) {
            this.updateOneTimePrice(variant);
            return;
          }
        }

        // Fallback: try to get variant data from the form's data attributes or wait for variant:change
        // If variant:change hasn't fired yet, wait a bit more
        setTimeout(() => {
          if (!this._oneTimePriceInitialized) {
            // Try one more time after a delay
            const variantPicker = document.querySelector('variant-picker[form="' + this.form.id + '"]');
            if (variantPicker && variantPicker.product) {
              const variant = variantPicker.product.variants.find(v => v.id === parseInt(variantId));
              if (variant) {
                this.updateOneTimePrice(variant);
                this._oneTimePriceInitialized = true;
              }
            }
          }
        }, 500);
      };

      checkVariant();
    }, 100);
  }

  updateOneTimePrice (variant) {
    if (!variant) return;

    this._oneTimePriceInitialized = true;

    const oneTimePriceElement = this.querySelector('[data-one-time-price]');
    const oneTimeComparePriceElement = this.querySelector('[data-one-time-compare-price]');
    const oneTimeInput = this.querySelector('[data-subscription-type="one-time"] input[data-selling-plan]');
    const oneTimeServingWrapper = this.querySelector('[data-one-time-serving-wrapper]');
    const oneTimeServingComparePrice = this.querySelector('[data-one-time-serving-compare-price]');
    const oneTimeServingFinalPrice = this.querySelector('[data-one-time-serving-final-price]');

    // Use formatMoney function from theme (should be available globally)
    const formatPrice = (cents) => {
      if (typeof formatMoney === 'function') {
        return formatMoney(cents);
      }
      // Fallback: simple formatting
      return '$' + (cents / 100).toFixed(2);
    };

    if (oneTimePriceElement) {
      oneTimePriceElement.textContent = formatPrice(variant.price);
    }

    if (oneTimeComparePriceElement) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        oneTimeComparePriceElement.textContent = formatPrice(variant.compare_at_price);
        oneTimeComparePriceElement.style.display = '';
      } else {
        oneTimeComparePriceElement.style.display = 'none';
      }
    }

    // Update serving price if data is available
    const oneTimePriceContainer = this.querySelector('[data-subscription-type="one-time"] .product-subscriptions__subscription-price-container');
    if (oneTimePriceContainer) {
      // Get price_per_serving data from the JSON script tag
      const pricePerServingScript = this.querySelector('[data-variant-price-per-serving]');
      if (pricePerServingScript) {
        try {
          const variantPricePerServing = JSON.parse(pricePerServingScript.textContent);
          const pricePerServing = variantPricePerServing[variant.id.toString()];
          
          // Remove existing wrapper if it exists
          const existingWrapper = oneTimePriceContainer.querySelector('[data-one-time-serving-wrapper]');
          if (existingWrapper) {
            existingWrapper.remove();
          }
          
          // Add wrapper if variant has price_per_serving data
          if (pricePerServing) {
            const wrapper = document.createElement('p');
            wrapper.className = 'product-subscriptions__subscription-price-wrapper';
            wrapper.setAttribute('data-one-time-serving-wrapper', '');
            
            // Add final price (price_per_serving is already in cents)
            const finalPrice = document.createElement('span');
            finalPrice.className = 'product-subscriptions__subscription-serve-price';
            finalPrice.setAttribute('data-one-time-serving-final-price', '');
            finalPrice.textContent = formatPrice(pricePerServing);
            wrapper.appendChild(finalPrice);
            
            // Add label - get from existing subscription options or use fallback
            const existingLabel = this.querySelector('.product-subscriptions__subscription-price-label');
            const label = document.createElement('span');
            label.className = 'product-subscriptions__subscription-price-label';
            label.textContent = existingLabel ? existingLabel.textContent : 'per serving';
            wrapper.appendChild(label);
            
            // Append to container
            oneTimePriceContainer.appendChild(wrapper);
          }
        } catch (e) {
          console.error('Error parsing price_per_serving data:', e);
        }
      }
    }

    // Update the data-variant-id on the one-time purchase input
    if (oneTimeInput) {
      oneTimeInput.setAttribute('data-variant-id', variant.id);
    }
  }
}

if (typeof window.customElements.get('product-subscriptions') === 'undefined') {
  window.customElements.define('product-subscriptions', productSubscriptions)
}
